import { ReactComponent as IconRadiusDistance } from "assets/icons/icon-radius-distance.svg";
import { ReactComponent as IconDrivingDistance } from "assets/icons/icon-driving-distance.svg";
import { ReactComponent as IconDrivingTime } from "assets/icons/icon-driving-time.svg";
import { ReactComponent as DirectionsWalkIcon } from "assets/icons/icon-walking-time.svg";

import { useDataFetch } from "hooks/useDataFetch";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect } from "react";
import { selectOAuthCredentials } from "@carto/react-redux";
import {
  getIsoline,
  ISOLINE_MODES,
  ISOLINE_LABELS,
  TRADE_AREA_LABELS,
} from "data/models/IsolinesModel";

import {
  setError,
  setIsoline,
  setIsolineRange,
  setIsolineMode,
  setIsolineLoading,
  setSelectedFeature,
  selectIsolineMarks,
  selectIsolineUnit,
  selectSelectedIsolineRange,
  selectSelectedLocation,
  selectIsolineFullUnit,
  selectIsolineMode,
} from "store/appSlice";
import bboxToViewState from "utils/bboxToViewState";
import {
  Box,
  Divider,
  fade,
  FormControl,
  Grid,
  makeStyles,
  MenuItem,
  Select,
  SvgIcon,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { setViewState } from "@carto/react-redux";
import bbox from "@turf/bbox";

const useStyles = makeStyles((theme) => ({
  areaSelectWrapper: {
    paddingLeft: theme.spacing(1.5),
  },
  buttonGroup: {
    marginRight: theme.spacing(1.5),
  },
  buttonGroupItem: {
    color: theme.palette.text.secondary,
    margin: 0,
    marginRight: theme.spacing(0.25),
    border: "none",
    "&:not(:first-child)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-child": {
      borderRadius: theme.shape.borderRadius,
      marginLeft: 0,
    },
    "&:last-child": {
      marginRight: 0,
    },
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.primary.main, 0.08),
    },
  },

  wrapper: {
    backgroundColor: theme.palette.background.default,
  },
  content: {
    padding: theme.spacing(0.5, 3, 3, 3),
  },
  contentCollapseed: {
    // display: 'none'
  },
  title: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1, 3),
  },
  titleValue: {
    marginLeft: theme.spacing(0.5),
  },
  titleCollapsed: {
    position: "absolute",
    zIndex: 3,
    top: 116,
    left: 0,
    right: 0,
  },
}));

export default function IsolinesConfigSelector({ collapsed }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentViewState = useSelector((state) => state.carto.viewState);
  const mode = useSelector(selectIsolineMode);
  const range = useSelector(selectSelectedIsolineRange);
  //const range = 500;
  const details = useSelector(selectSelectedLocation);
  const unit = useSelector(selectIsolineUnit(mode));
  const fullUnit = useSelector(selectIsolineFullUnit(mode));
  const label = ISOLINE_LABELS[mode];
  const isolineMarks = useSelector(selectIsolineMarks(mode));
  const sliderOptions = isolineMarks.map((n) => ({
    value: n,
    label:
      mode === ISOLINE_MODES.ZIPCODES
        ? TRADE_AREA_LABELS[n]
        : `${n.toFixed(0)} ${unit}`,
  }));

  // const selectedValueLabel = sliderOptions.find((opt) => opt.value === range)?.label;

  const { data, loading, error } = useDataFetch({
    initialData: null,
    callback: useCallback(
      ({ credentials, abortController }) => {
        const center = [details?.longitude, details?.latitude];
        const params = {
          center,
          range,
          mode,
          unit: fullUnit,
          abortController,
        };

        return getIsoline(credentials, params);
      },
      [range, mode, fullUnit, details?.longitude, details?.latitude]
    ),
    use404: false,
    enabled: !!(details && currentViewState.width && currentViewState.height),
    errorMsg: "[PoiHeader.js] Error fetching isolines",
  });

  useEffect(() => {
    if (error) dispatch(setError(error.message));
  }, [error, dispatch]);

  useEffect(() => {
    dispatch(setIsolineLoading(loading));
  }, [dispatch, loading]);

  useEffect(() => {
    if (data) {
      const viewState = bboxToViewState(currentViewState, bbox(data), {
        padding: 50,
      });
      dispatch(
        setViewState({
          ...viewState,
          hasFlight: true,
          transitionDuration: 2,
          dragRotate: true,
          pitch: 55,
        })
      );
      dispatch(setIsoline(data));
    }

    return () => {
      dispatch(setIsoline(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, data]);

  function handleRangeChange(ev) {
    const range = ev.target.value;
    dispatch(setIsolineRange(range));

    if (mode === ISOLINE_MODES.RADIUS) {
      dispatch(setSelectedFeature({ radius: range }));
    }
  }

  function handleToggleGroup(ev, val) {
    if (val) {
      dispatch(setIsolineMode(val));
      dispatch(setIsolineRange(null));
    }
  }

  return (
    <Box className={classes.wrapper}>
      <IsolineTitle collapsed={collapsed} label={label} />
      <Grid
        item
        container
        wrap="nowrap"
        direction="row"
        alignItems="center"
        justify="space-between"
        className={`${classes.content} ${
          collapsed ? classes.contentCollapseed : ""
        }`}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          classes={{
            root: classes.buttonGroup,
            grouped: classes.buttonGroupItem,
          }}
          onChange={handleToggleGroup}
          aria-label="isoline mode"
        >
          <Tooltip
            id="radius-label"
            title={`by ${ISOLINE_LABELS[ISOLINE_MODES.RADIUS]}`}
            placement="top"
            arrow
          >
            <ToggleButton
              selected={mode === ISOLINE_MODES.RADIUS}
              value={ISOLINE_MODES.RADIUS}
              aria-labelledby="radius-label"
            >
              <SvgIcon component={IconRadiusDistance} viewBox="0 0 24 24" />
            </ToggleButton>
          </Tooltip>
          <Tooltip
            id="isodistance-label"
            title={`by ${ISOLINE_LABELS[ISOLINE_MODES.WALK]}`}
            placement="top"
            arrow
          >
            <ToggleButton
              selected={mode === ISOLINE_MODES.WALK}
              value={ISOLINE_MODES.WALK}
              aria-labelledby="isodistance-label"
            >
              <SvgIcon component={DirectionsWalkIcon} viewBox="0 0 24 24" />
            </ToggleButton>
          </Tooltip>
          <Tooltip
            id="isochrone-label"
            title={`by ${ISOLINE_LABELS[ISOLINE_MODES.TIME]}`}
            placement="top"
            arrow
          >
            <ToggleButton
              selected={mode === ISOLINE_MODES.TIME}
              value={ISOLINE_MODES.TIME}
              aria-labelledby="isochrone-label"
            >
              <SvgIcon component={IconDrivingTime} viewBox="0 0 24 24" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
        <Divider orientation="vertical" flexItem />
        <Grid item xs className={classes.areaSelectWrapper}>
          <FormControl size="small">
            <Select
              aria-labelledby="area-select-label"
              placeholder="Select area"
              value={range}
              displayEmpty={true}
              onChange={handleRangeChange}
            >
              {sliderOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

function IsolineTitle({ collapsed, label, value }) {
  const classes = useStyles();
  return (
    <Box
      lineHeight={1}
      mt={0.25}
      id="area-select-label"
      className={`${classes.title} ${collapsed ? classes.titleCollapsed : ""}`}
    >
      <Typography variant="caption">Area by {label}</Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        className={classes.titleValue}
      >
        {value}
      </Typography>
    </Box>
  );
}
